import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { RefreshCw, ChevronLeft, ChevronRight, Calendar, TrendingUp, Activity, X, Users, Heart, UserPlus, BarChart3 } from 'lucide-react';
import axios from 'axios';
import SharedPageHeader from '../components/layout/PageHeader';
import useStickyPin from '../hooks/useStickyPin';

const PageContainer = styled.div`
  padding: 20px;
`;





const Controls = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const RefreshButton = styled.button`
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  border: 1px solid rgba(165, 145, 239, 0.4);
  border-radius: 8px;
  padding: 8px 18px;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(165, 145, 239, 0.3);
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 14px;
  margin-bottom: 20px;
`;

const StatCard = styled.div`
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 10px;
  padding: 16px;
  transition: all 0.15s;

  &:hover { border-color: ${props => props.$color || 'var(--primary)'}; }
`;

const StatLabel = styled.div`
  color: var(--text-muted);
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const StatValue = styled.div`
  color: ${props => props.$color || 'var(--text-primary)'};
  font-size: 24px;
  font-weight: 800;
`;

const DateFilterContainer = styled.div`
  background: var(--card-bg);
  border-radius: 10px;
  border: 1px solid var(--card-border);
  padding: 14px 18px;
  margin-bottom: 16px;
`;

const DateFilterRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-end;
  flex-wrap: wrap;
`;

const DateFilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DateFilterLabel = styled.label`
  color: var(--text-secondary);
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const DateModeSelect = styled.select`
  background: var(--surface);
  border: 1px solid var(--card-border);
  border-radius: 8px;
  padding: 8px 12px;
  color: var(--text-primary);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;

  &:focus {
    outline: none;
    border-color: var(--primary);
  }

  option {
    background: var(--bg);
    color: var(--text-primary);
  }
`;

const DateInput = styled.input`
  background: var(--surface);
  border: 1px solid var(--card-border);
  border-radius: 8px;
  padding: 8px 12px;
  color: var(--text-primary);
  font-size: 13px;
  font-weight: 600;
  transition: all 0.15s;

  &:focus {
    outline: none;
    border-color: var(--primary);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

const ResetButton = styled.button`
  background: var(--surface);
  border: 1px solid var(--card-border);
  border-radius: 8px;
  padding: 8px 14px;
  color: var(--text-secondary);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    background: var(--error);
    border-color: var(--error);
    color: #fff;
  }
`;

const TableContainer = styled.div`
  background: var(--card-bg);
  border-radius: 10px;
  border: 1px solid var(--card-border);
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHead = styled.thead`
  background: var(--surface);
  border-bottom: 1px solid var(--card-border);
`;

const TableRow = styled.tr`
  background: ${props => props.$bgColor || 'var(--card-bg)'};
  border-left: 3px solid ${props => props.$borderColor || 'transparent'};

  &:nth-child(even) {
    background: ${props => props.$bgColor || 'var(--surface)'};
  }

  &:hover {
    background: var(--surface-hover);
  }

  transition: background 0.1s;
`;

const TableHeader = styled.th`
  padding: 10px 14px;
  text-align: left;
  color: var(--text-muted);
  font-weight: 700;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid var(--card-border);
  white-space: nowrap;
  vertical-align: top;
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 120px;
`;

const HeaderTitle = styled.div`
  color: var(--text-muted);
  font-weight: 700;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
`;

const HeaderFilter = styled.input`
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 6px;
  padding: 6px 10px;
  color: var(--text-primary);
  font-size: 12px;
  font-weight: 500;
  transition: all 0.2s ease;
  width: 100%;

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 2px rgba(88, 101, 242, 0.2);
  }

  &::placeholder {
    color: var(--text-muted);
    font-size: 11px;
  }
`;

const HeaderSelect = styled.select`
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 6px;
  padding: 6px 10px;
  color: var(--text-primary);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 2px rgba(88, 101, 242, 0.2);
  }

  option {
    background: var(--bg);
    color: var(--text-primary);
  }
`;

const TableCell = styled.td`
  padding: 10px 14px;
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: 500;
  border-bottom: 1px solid var(--card-border);
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &.highlight {
    color: var(--text-primary);
    font-weight: 600;
  }

  &.type {
    font-weight: 600;
  }

  &.source {
    color: var(--success);
    font-weight: 600;
  }

  &.target {
    color: var(--info);
    font-weight: 600;
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: var(--card-bg);
  border-top: 1px solid var(--card-border);
`;

const PageInfo = styled.div`
  color: var(--text-muted);
  font-weight: 600;
  font-size: 12px;
`;

const PageControls = styled.div`
  display: flex;
  gap: 8px;
`;

const PageButton = styled.button`
  background: ${props => props.$active ? 'var(--primary)' : 'var(--surface)'};
  border: 1px solid ${props => props.$active ? 'var(--primary)' : 'var(--card-border)'};
  border-radius: 6px;
  padding: 6px 12px;
  color: ${props => props.$active ? '#fff' : 'var(--text-secondary)'};
  font-weight: 600;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.15s;

  &:hover:not(:disabled) {
    background: var(--primary);
    color: #fff;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 60px;
  color: var(--text-secondary);
  font-size: 18px;
  font-weight: 700;
`;

const ActionBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 6px;
  background: ${props => props.$color || 'var(--primary)'};
  color: #fff;
  font-weight: 700;
  font-size: 11px;
  white-space: nowrap;
`;

const ActionsLog = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);
  const [stats, setStats] = useState(null);
  const [accounts, setAccounts] = useState([]);
  
  // Filtres
  const [filterType, setFilterType] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [filterTarget, setFilterTarget] = useState('');
  const [filterAccount, setFilterAccount] = useState('');
  const [dateMode, setDateMode] = useState('today');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    setStartDate(today);
    setEndDate(today);
    fetchAccounts();
  }, []);
  
  const fetchAccounts = async () => {
    try {
      const response = await axios.get('/api/instagram-accounts');
      setAccounts(response.data.accounts || []);
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des comptes:', error);
    }
  };
  
  const itemsPerPage = 50;

  useEffect(() => {
    fetchLogs();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchTerm, filterType, filterSource, filterTarget, filterAccount, startDate, endDate]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm,
        type: filterType,
        source: filterSource,
        target: filterTarget,
        account: filterAccount,
        startDate: startDate,
        endDate: endDate
      };
      
      console.log('📤 Envoi requête API avec params:', params);
      
      const response = await axios.get('/api/actions-log', { params });
      
      console.log('📥 Réponse API:', {
        total: response.data.total,
        logs: response.data.logs.length,
        stats: response.data.stats
      });
      
      setLogs(response.data.logs);
      setTotalPages(response.data.totalPages);
      setTotalLogs(response.data.total);
      setStats(response.data.stats);
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleString('fr-FR', {
      timeZone: 'Europe/Paris',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getActionConfig = (actionType) => {
    const configs = {
      'LikeAction': { label: 'Post liké', color: 'rgba(239, 68, 68, 0.7)', bgColor: 'rgba(239, 68, 68, 0.08)' },
      'FollowAction': { label: 'Abonnement', color: 'rgba(165, 145, 239, 0.7)', bgColor: 'rgba(165, 145, 239, 0.08)' },
      'UnfollowAction': { label: 'Désabonnement', color: 'rgba(107, 114, 128, 0.7)', bgColor: 'rgba(107, 114, 128, 0.08)' },
      'CommentAction': { label: 'Commentaire', color: 'rgba(59, 130, 246, 0.7)', bgColor: 'rgba(59, 130, 246, 0.08)' },
      'DirectMessageAction': { label: 'Message direct', color: 'rgba(16, 185, 129, 0.7)', bgColor: 'rgba(16, 185, 129, 0.08)' },
      'StoryLikeAction': { label: 'Story likée', color: 'rgba(249, 115, 22, 0.7)', bgColor: 'rgba(249, 115, 22, 0.08)' },
      'StoryWatchAction': { label: 'Story vue', color: 'rgba(234, 179, 8, 0.7)', bgColor: 'rgba(234, 179, 8, 0.08)' },
      'GetProfileAction': { label: 'Profil visité', color: 'rgba(6, 182, 212, 0.7)', bgColor: 'rgba(6, 182, 212, 0.08)' }
    };
    return configs[actionType] || { label: actionType, color: 'rgba(165, 145, 239, 0.7)', bgColor: 'rgba(165, 145, 239, 0.08)' };
  };

  const handleDateModeChange = (mode) => {
    setDateMode(mode);
    const today = new Date().toISOString().split('T')[0];
    
    if (mode === 'today') {
      setStartDate(today);
      setEndDate(today);
    } else if (mode === 'single') {
      setStartDate(today);
      setEndDate(today);
    } else if (mode === 'range') {
      setStartDate(today);
      setEndDate(today);
    } else if (mode === 'all') {
      setStartDate('');
      setEndDate('');
    }
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setFilterType('');
    setFilterSource('');
    setFilterTarget('');
    setFilterAccount('');
    setDateMode('today');
    const today = new Date().toISOString().split('T')[0];
    setStartDate(today);
    setEndDate(today);
    setCurrentPage(1);
  };

  const { sectionRef, getStickyStyle } = useStickyPin('actionslog', ['header', 'stats']);

  return (
    <PageContainer>
      <div ref={sectionRef('header')} style={{ ...getStickyStyle('header'), background: 'var(--surface)' }}>
      <SharedPageHeader icon={Activity} title="Actions Log" subtitle="Journal des actions Instagram">
        <Controls>
          <RefreshButton onClick={fetchLogs}>
            <RefreshCw size={18} />
            Actualiser
          </RefreshButton>
        </Controls>
      </SharedPageHeader>
      </div>

      {stats && (
        <div ref={sectionRef('stats')} style={{ ...getStickyStyle('stats'), background: 'var(--surface)' }}>
        <StatsContainer>
          <StatCard $color="var(--primary)">
            <StatLabel>
              <Activity size={18} />
              Total Actions
            </StatLabel>
            <StatValue $color="var(--primary)">{stats.totalActions || 0}</StatValue>
          </StatCard>
          <StatCard $color="var(--success)">
            <StatLabel>
              <Users size={18} />
              Comptes Actifs
            </StatLabel>
            <StatValue $color="var(--success)">{stats.totalAccounts || 0}</StatValue>
          </StatCard>
          <StatCard $color="var(--warning)">
            <StatLabel>
              <TrendingUp size={18} />
              Moyenne / Jour
            </StatLabel>
            <StatValue $color="var(--warning)">{stats.avgPerDay || 0}</StatValue>
          </StatCard>
          <StatCard $color="var(--primary)">
            <StatLabel>
              <BarChart3 size={18} />
              Moy. / Compte / Jour
            </StatLabel>
            <StatValue $color="var(--primary)">{stats.avgPerAccountPerDay || 0}</StatValue>
          </StatCard>
          <StatCard $color="var(--error)">
            <StatLabel>
              <Heart size={18} />
              Moy. Likes / Compte
            </StatLabel>
            <StatValue $color="var(--error)">{stats.avgLikesPerAccount || 0}</StatValue>
          </StatCard>
          <StatCard $color="var(--primary)">
            <StatLabel>
              <UserPlus size={18} />
              Moy. Follows / Compte
            </StatLabel>
            <StatValue $color="var(--primary)">{stats.avgFollowsPerAccount || 0}</StatValue>
          </StatCard>
          <StatCard $color="var(--info)">
            <StatLabel>
              <TrendingUp size={18} />
              Follows / Compte / Jour
            </StatLabel>
            <StatValue $color="var(--info)">{stats.avgFollowsPerAccountPerDay || 0}</StatValue>
          </StatCard>
          <StatCard $color="var(--warning)">
            <StatLabel>
              <Calendar size={18} />
              Nombre de Jours
            </StatLabel>
            <StatValue $color="var(--warning)">{stats.totalDays || 0}</StatValue>
          </StatCard>
        </StatsContainer>
        </div>
      )}

      <DateFilterContainer>
        <DateFilterRow>
          <DateFilterGroup>
            <DateFilterLabel>Mode de filtrage</DateFilterLabel>
            <DateModeSelect value={dateMode} onChange={(e) => handleDateModeChange(e.target.value)}>
              <option value="today">Aujourd'hui</option>
              <option value="single">Jour spécifique</option>
              <option value="range">Période</option>
              <option value="all">Toutes les dates</option>
            </DateModeSelect>
          </DateFilterGroup>

          {(dateMode === 'single' || dateMode === 'range') && (
            <DateFilterGroup>
              <DateFilterLabel>{dateMode === 'single' ? 'Date' : 'Date de début'}</DateFilterLabel>
              <DateInput
                type="date"
                value={startDate}
                onChange={(e) => { setStartDate(e.target.value); if (dateMode === 'single') setEndDate(e.target.value); setCurrentPage(1); }}
              />
            </DateFilterGroup>
          )}

          {dateMode === 'range' && (
            <DateFilterGroup>
              <DateFilterLabel>Date de fin</DateFilterLabel>
              <DateInput
                type="date"
                value={endDate}
                onChange={(e) => { setEndDate(e.target.value); setCurrentPage(1); }}
              />
            </DateFilterGroup>
          )}

          <DateFilterGroup>
            <DateFilterLabel>&nbsp;</DateFilterLabel>
            <ResetButton onClick={resetFilters}>
              <X size={18} />
              Réinitialiser
            </ResetButton>
          </DateFilterGroup>
        </DateFilterRow>
      </DateFilterContainer>

      {loading ? (
        <LoadingState>⏳ Chargement des logs...</LoadingState>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <tr>
                <TableHeader>
                  <HeaderContent>
                    <HeaderTitle>ID</HeaderTitle>
                  </HeaderContent>
                </TableHeader>
                <TableHeader>
                  <HeaderContent>
                    <HeaderTitle>Compte Instagram</HeaderTitle>
                    <HeaderSelect
                      value={filterAccount}
                      onChange={(e) => { setFilterAccount(e.target.value); setCurrentPage(1); }}
                    >
                      <option value="">Tous les comptes</option>
                      {accounts.map(account => (
                        <option key={account.id} value={account.instagramUsername}>
                          {account.instagramUsername}
                        </option>
                      ))}
                    </HeaderSelect>
                  </HeaderContent>
                </TableHeader>
                <TableHeader>
                  <HeaderContent>
                    <HeaderTitle>Type</HeaderTitle>
                    <HeaderSelect value={filterType} onChange={(e) => { setFilterType(e.target.value); setCurrentPage(1); }}>
                      <option value="">Tous</option>
                      <option value="LikeAction">Post liké</option>
                      <option value="FollowAction">Abonnement</option>
                      <option value="UnfollowAction">Désabonnement</option>
                      <option value="CommentAction">Commentaire</option>
                      <option value="DirectMessageAction">Message</option>
                      <option value="StoryLikeAction">Story likée</option>
                      <option value="StoryWatchAction">Story vue</option>
                      <option value="GetProfileAction">Profil visité</option>
                    </HeaderSelect>
                  </HeaderContent>
                </TableHeader>
                <TableHeader>
                  <HeaderContent>
                    <HeaderTitle>Target User</HeaderTitle>
                    <HeaderFilter
                      type="text"
                      placeholder="Filtrer..."
                      value={filterTarget}
                      onChange={(e) => setFilterTarget(e.target.value)}
                      onBlur={() => setCurrentPage(1)}
                    />
                  </HeaderContent>
                </TableHeader>
                <TableHeader>
                  <HeaderContent>
                    <HeaderTitle>Source</HeaderTitle>
                    <HeaderFilter
                      type="text"
                      placeholder="Filtrer..."
                      value={filterSource}
                      onChange={(e) => setFilterSource(e.target.value)}
                      onBlur={() => setCurrentPage(1)}
                    />
                  </HeaderContent>
                </TableHeader>
                <TableHeader>
                  <HeaderContent>
                    <HeaderTitle>Date</HeaderTitle>
                  </HeaderContent>
                </TableHeader>
                <TableHeader>
                  <HeaderContent>
                    <HeaderTitle>Session ID</HeaderTitle>
                  </HeaderContent>
                </TableHeader>
              </tr>
            </TableHead>
            <tbody>
              {logs.map((log) => {
                const actionConfig = getActionConfig(log.type);
                const hoverColor = actionConfig.bgColor.replace('0.08', '0.15');
                return (
                  <TableRow 
                    key={log.id}
                    $bgColor={actionConfig.bgColor}
                    $borderColor={actionConfig.color}
                    $hoverColor={hoverColor}
                  >
                    <TableCell className="highlight">{log.id}</TableCell>
                    <TableCell className="highlight">{log.instagram_username || '-'}</TableCell>
                    <TableCell className="type">
                      <ActionBadge $color={actionConfig.color}>
                        {actionConfig.label}
                      </ActionBadge>
                    </TableCell>
                    <TableCell className="target">{log.target_user || '-'}</TableCell>
                    <TableCell className="source">{log.source || '-'}</TableCell>
                    <TableCell>{formatDate(log.timestamp)}</TableCell>
                    <TableCell className="highlight">{log.session_id || '-'}</TableCell>
                  </TableRow>
                );
              })}
            </tbody>
          </Table>

          <Pagination>
            <PageInfo>
              {totalLogs} logs au total • Page {currentPage} sur {totalPages}
            </PageInfo>
            <PageControls>
              <PageButton
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={18} />
              </PageButton>
              <PageButton $active>
                {currentPage}
              </PageButton>
              <PageButton
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
              >
                <ChevronRight size={18} />
              </PageButton>
            </PageControls>
          </Pagination>
        </TableContainer>
      )}
    </PageContainer>
  );
};

export default ActionsLog;
